import { toTenThousandths } from "@repo/shared";
import { useRef, useState } from "react";

/** One portfolio as a grid column: the weights it holds, keyed by product id. */
export type WeightsGridColumn = {
  /** Id of the portfolio the column edits. */
  id: string;
  cells: Record<string, { rowId: string; weight: number }>;
  total: number;
  hasProducts: boolean;
  isIncomplete: boolean;
  /** Shown under the risk score when it says more than the score does. */
  inlineName: string | null;
  tooltipTitle: string;
  tooltipDescription: string | null;
};

export type WeightsGridHandlers = {
  addCell: (columnId: string, productId: string, weight: number) => Promise<unknown>;
  updateCell: (columnId: string, rowId: string, weight: number) => Promise<unknown>;
  deleteCell: (columnId: string, rowId: string) => Promise<unknown>;
  /** Creates the portfolio of a risk score that has none; resolves to its id. */
  createColumn: (score: number) => Promise<string>;
  /**
   * Creates the portfolio of a risk score that has none together with its
   * first weight, so a refused weight leaves no empty portfolio behind;
   * resolves to its id. Without it the portfolio is created first.
   */
  createColumnWithCell?: (score: number, productId: string, weight: number) => Promise<string>;
};

type UseWeightsGridAutosaveOptions = {
  /** The portfolios of each risk score, in display order. */
  columns: Map<number, WeightsGridColumn[]>;
  handlers: WeightsGridHandlers;
  onError: (message: string) => void;
  messages: { weightOutOfRange: string; failedToSave: string };
};

// A column is addressed by its risk score and its position within the score,
// which stays the same when the portfolio of an empty column gets created.
const columnKey = (score: number, index: number) => `${score}:${index}`;
const cellKey = (productId: string, score: number, index: number) =>
  `${productId}:${columnKey(score, index)}`;

/**
 * Draft state and save-on-blur of the weights grid. A cleared or zero cell
 * removes the product, a changed one updates it, a new one adds it — creating
 * the column's portfolio first when the risk score has none.
 */
export function useWeightsGridAutosave({
  columns,
  handlers,
  onError,
  messages,
}: UseWeightsGridAutosaveOptions) {
  // Local string mirror so the user can type freely.
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [savingKeys, setSavingKeys] = useState<ReadonlySet<string>>(new Set());

  // Saves of one column run one after another: the second cell typed into a
  // column without a portfolio must add to the portfolio the first one created.
  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const queues = useRef(new Map<string, Promise<void>>());
  const createdColumnIds = useRef(new Map<string, string>());

  const dropDraft = (key: string) =>
    setDraft((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });

  const cellValue = (productId: string, score: number, index: number): string => {
    const key = cellKey(productId, score, index);
    if (key in draft) return draft[key] ?? "";
    const cell = columns.get(score)?.[index]?.cells[productId];
    return cell ? String(cell.weight) : "";
  };

  const setValue = (productId: string, score: number, index: number, value: string) =>
    setDraft((current) => ({ ...current, [cellKey(productId, score, index)]: value }));

  const save = async (productId: string, score: number, index: number, weight: number | null) => {
    const column = columnsRef.current.get(score)?.[index];
    const cell = column?.cells[productId];
    const columnId = column?.id ?? createdColumnIds.current.get(columnKey(score, index));

    if (weight === null) {
      if (cell && columnId) await handlers.deleteCell(columnId, cell.rowId);
      return;
    }
    if (cell && columnId) {
      if (toTenThousandths(cell.weight) !== toTenThousandths(weight)) {
        await handlers.updateCell(columnId, cell.rowId, weight);
      }
      return;
    }
    if (columnId) {
      await handlers.addCell(columnId, productId, weight);
      return;
    }
    if (handlers.createColumnWithCell) {
      const createdId = await handlers.createColumnWithCell(score, productId, weight);
      createdColumnIds.current.set(columnKey(score, index), createdId);
      return;
    }
    const createdId = await handlers.createColumn(score);
    createdColumnIds.current.set(columnKey(score, index), createdId);
    await handlers.addCell(createdId, productId, weight);
  };

  const persist = (productId: string, score: number, index: number) => {
    const key = cellKey(productId, score, index);
    const raw = draft[key];
    if (raw === undefined) return;

    const trimmed = raw.trim();
    const parsed = trimmed === "" ? Number.NaN : Number(trimmed);
    const isEmpty = Number.isNaN(parsed);

    if (!isEmpty && (parsed < 0 || parsed > 100)) {
      onError(messages.weightOutOfRange);
      dropDraft(key);
      return;
    }

    // Empty and 0 both mean "not in this portfolio".
    const weight = isEmpty || parsed === 0 ? null : parsed;

    setSavingKeys((current) => new Set(current).add(key));
    const run = async () => {
      try {
        await save(productId, score, index, weight);
      } catch (err) {
        onError(err instanceof Error ? err.message : messages.failedToSave);
      } finally {
        dropDraft(key);
        setSavingKeys((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        });
      }
    };
    const queueKey = columnKey(score, index);
    const queued = (queues.current.get(queueKey) ?? Promise.resolve()).then(run);
    queues.current.set(queueKey, queued);
  };

  return {
    cellValue,
    setValue,
    persist,
    isSaving: (productId: string, score: number, index: number) =>
      savingKeys.has(cellKey(productId, score, index)),
  };
}
